import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { FixtureStatus, type H2HFixture, type League } from '../types'
import { teamSquadLink } from '../teamSquadLink'
import styles from './FixturesPanel.module.css'

type FixturesPanelProps = {
  league: League
  fixtures: H2HFixture[]
}

type TeamNameProps = {
  league: League
  userId: string | null   // null = "Média" ghost participant — never linkable
  teamName: string
  align?: 'right'
}

function TeamName({ league, userId, teamName, align }: TeamNameProps) {
  const link = userId ? teamSquadLink(userId, league) : null
  const className = clsx(styles.team, align === 'right' && styles.teamRight)

  if (!link) {
    return <span className={className}>{teamName}</span>
  }

  return (
    <Link
      to="/league/$leagueId/team/$userId"
      params={{ leagueId: league.id, userId: link.userId }}
      search={{ gw: link.gw }}
      className={clsx(className, styles.teamLink)}
    >
      {teamName}
    </Link>
  )
}

function FixtureRow({ league, fixture }: { league: League; fixture: H2HFixture }) {
  const isFinished = fixture.status === FixtureStatus.Finished
  const isLive = fixture.status === FixtureStatus.Live

  return (
    <div className={clsx(styles.fixture, fixture.isCurrentUserMatch && styles.isCurrentUser)}>
      <TeamName league={league} userId={fixture.homeUserId} teamName={fixture.homeTeamName} />
      <div className={styles.score}>
        {isFinished || isLive ? (
          <>
            <span className={styles.pts}>{fixture.homePoints ?? '—'}</span>
            <span className={styles.vs}>×</span>
            <span className={styles.pts}>{fixture.awayPoints ?? '—'}</span>
          </>
        ) : (
          <span className={styles.upcoming}>vs</span>
        )}
        {isLive && <span className={styles.liveBadge}>AO VIVO</span>}
      </div>
      <TeamName league={league} userId={fixture.awayUserId} teamName={fixture.awayTeamName} align="right" />
    </div>
  )
}

export function FixturesPanel({ league, fixtures }: FixturesPanelProps) {
  const currentGameweek = league.gwStatus.currentGameweek
  const gameweeks = [...new Set(fixtures.map((f) => f.gameweek))].sort((a, b) => b - a)
  const [selectedGw, setSelectedGw] = useState<number>(
    currentGameweek ?? gameweeks[0] ?? 1,
  )

  const filtered = fixtures.filter((f) => f.gameweek === selectedGw)

  if (fixtures.length === 0) {
    return (
      <div className={styles.empty}>
        <p>Nenhum confronto disponível.</p>
      </div>
    )
  }

  return (
    <div className={styles.panel}>
      {/* Gameweek selector */}
      <div className={styles.gwSelector}>
        {gameweeks.map((gw) => (
          <button
            key={gw}
            className={clsx(styles.gwBtn, selectedGw === gw && styles.gwBtnActive)}
            onClick={() => setSelectedGw(gw)}
          >
            R{gw}
          </button>
        ))}
      </div>

      {/* Fixtures list */}
      <div className={styles.list}>
        {filtered.map((f) => (
          <FixtureRow key={`${f.gameweek}-${f.homeUserId}`} league={league} fixture={f} />
        ))}
      </div>
    </div>
  )
}
